import { Injectable, Inject, Optional } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PershingTranslateLoader } from './pershing-translate-loader.service';
import { PershingHttpUtil } from '../utils/pershing-http.util';
import { map, catchError } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class PershingTranslationService {
    // userLang: string = 'en';
    defaultLang: string = 'en';
    userLang: string;
    customLoader: PershingTranslateLoader;

    constructor(public translate: TranslateService, @Optional() @Inject('AppSettings') private appSettings: any) {
        // this.userLang = translate.getBrowserLang() || 'en';
        this.userLang = this.defaultLang;
        // If language is passed in the appSettings , use that
        if (appSettings && appSettings.language && appSettings.language.length > 0) {
            this.userLang = appSettings.language;
        }

        this.customLoader = <PershingTranslateLoader>this.translate.currentLoader;
        // load defaultLang if userlang is different
        if (this.userLang !== this.defaultLang) {
            this.customLoader.init(this.defaultLang);
        }
        this.customLoader.init(this.userLang);
        translate.setDefaultLang(this.defaultLang);
        translate.use(this.userLang);
        if (this.appSettings && this.appSettings.translationOverrideFolder) {
            this.addTranslationFolders('site-level-translation-override-path', [this.appSettings.translationOverrideFolder]);
        }
    }

    addTranslationFolders(name: string = '', paths: string[] = [], parentKey: string = '') {
        let resourcePaths: string[] = [];
        for (let path of paths) {
            resourcePaths.push(PershingHttpUtil.resolveResourcePath(name, path, this.appSettings));
        }
        if (!this.customLoader.existComponent(name, parentKey)) {
            this.customLoader.addComponentList(name, resourcePaths, parentKey);
            if (this.userLang !== this.defaultLang) {
                // Load both languages when userLang is different from defaultLang
                forkJoin([this.translate.getTranslation(this.defaultLang), this.translate.getTranslation(this.userLang)]).subscribe(results => {
                    this.translate.onLangChange.emit({ lang: this.userLang, translations: results[1] });
                });
            } else {
                // this.translate.use(this.userLang);
                /**
                 *  When multiple component resources are loaded using addTranslationFolder (Eg: multiple components in same page)
                 *  and if there is a delay in loading resource for any of the component,
                 *  the labels in that component will not get resolved.
                 *  translate.use('') don't trigger a re-evaluation on that component, as the pipe
                 *  already finished translation for that component.
                 *  For the pipe to re-trigger the translation, LangChange event needs to be emitted.
                 *  Previously, translate.use was used. However, use method does not emit the event
                 *  if the language key is the same (as current language - eg: 'en').
                 *  So the component end up in having the key itself rendered as value.
                 */
                this.translate.getTranslation(this.userLang).subscribe(
                    data => {
                        this.translate.onLangChange.emit({ lang: this.userLang, translations: data });
                    });
            }
        }
    }

    addTranslationFolder(name: string = '', path: string = '', parentKey: string = '') {
        let pathArray: string[] = [path];
        return this.addTranslationFolders(name, pathArray, parentKey);
    }

    use(lang: string): Observable<any> {
        this.customLoader.init(lang);
        this.userLang = lang;
        return this.translate.use(lang);
    }

    get(key: string | Array<string>, interpolateParams?: Object): Observable<string | any> {
        return this.translate.get(key, interpolateParams);
    }

    addTranslationOverride(lang: string, key: string, value: string) {
        let override: Object = this.buildKeyObject(key.split('.'), value);
        this.customLoader.addTranslationOverride(lang, override);
        this.translate.reloadLang(lang);
        this.translate.onTranslationChange.emit({ lang: lang, translations: this.translate.translations });
    }

    private buildKeyObject(keyarray, value: string): any {
        if (keyarray) {
            if (keyarray.length && keyarray.length === 1) {
                let obj = {};
                obj[`${keyarray[0]}`] = `${value}`;
                return obj;
            } else {
                let obj = {};
                obj[`${keyarray[0]}`] = this.buildKeyObject(keyarray.slice(1), value);
                return obj;
            }
        }
    }

    instant(key: string, id: string = ''): any {
        let idKey = `${id}.${key}`;
        if (this.translate.instant(idKey) && this.translate.instant(idKey) !== idKey) {
            return this.translate.instant(idKey);
        } else {
            return this.translate.instant(key);
        }
    }
}
